<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { formatNumber, formatDate } from "@core/helpers"
import type { Subscription } from "@modules/espace-entreprise/types/subscription"
import { useAuthStore } from "@core/stores/auth.store"
import { storeToRefs } from "pinia"
import CompanySubscriptionDecompteTree from "./CompanySubscriptionDecompteTree.vue"
import CompanySubscriptionDecompteBarre from "./CompanySubscriptionDecompteBarre.vue"
import {
    texteEcart,
    texteFacturees,
    texteForfait,
    type FiltresLigne,
    textePeriode,
    compositionCoherente,
    offreServie,
    TEXTE_FACTUREES_INDISPONIBLE,
    type PlafondDecompte,
} from "@core/composables/useCompanyPlafond"
import { fournirDevenirSurvole } from "@core/composables/useDevenirSurvole"
import { lireDetailOuvert, memoriserDetailOuvert } from "./detailDecompte"

// Survol croisé de la barre et du tableau (maquette v2 du 16/10)
fournirDevenirSurvole()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            status: {
                active: "Abonnement actif",
                paused: "Abonnement en pause",
                canceled: "Abonnement annulé",
                past_due: "Actif, en retard de paiement",
                trialing: "Abonnement actif - Essai",
            },
            list_source: "Voir les sources",
            liberer_espace: "Libérer de l'espace",
            voir_detail: "Voir le détail",
            masquer_detail: "Masquer le détail",
            module_price: "+ {value} / {interval}",
            fields: {
                price: "Prix",
                facturation: "Facturation",
                start_date: "Date de début de l'abonnement",
                end_date: "Date de fin",
            },
            facturation: {
                month: "Mensuel",
                year: "Annuel",
                week: "Hebdomadaire",
                day: "Quotidien",
                quote: "Devis",
            },
            end_date_stripe: "Prochaine facturation",
            end_date_no_stripe: "Date de fin",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Détail de l'abonnement
     */
    subscription: Subscription
    /**
     * Nombre de sources facturées servi, `null` s'il n'est pas servi
     * (aucun nombre n'est alors écrit)
     */
    sources: number | null
    /**
     * Décompte servi par l'API (`plafond`), `null` tant qu'il n'est pas servi
     */
    plafond?: PlafondDecompte | null
    /**
     * Vrai une fois la réponse du décompte arrivée (avant : aucun nombre)
     */
    decompteLoaded?: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    plafond: null,
    decompteLoaded: true,
})

defineEmits<{
    "click:source": []
    /** Ouvre la liste filtrée sur les sources qui libèrent une place (DEV-2480) */
    "click:liberer": []
    /** Ouvre la page des sources filtrée sur les sources d'une ligne du tableau */
    "click:filtres": [filtres: FiltresLigne]
}>()

// ================================
// STORES
// ================================
const authStore = useAuthStore()

const { isCompanyAdmin } = storeToRefs(authStore)

// ================================
// STATE
// ================================

/**
 * Carte dans la mémoire du dépliage : l'identifiant de l'abonnement, sinon
 * son nom
 */
const cleCarte = (): string =>
    props.subscription.idsubscription ?? props.subscription.name

/**
 * Tableau des critères déplié (DEV-2480) : replié par défaut, le choix tient
 * le temps de la session au plus, carte par carte
 */
const detailOuvert = ref(lireDetailOuvert(cleCarte()))

// ================================
// METHODS
// ================================

/** « Voir le détail » / « Masquer le détail » */
function basculerDetail() {
    detailOuvert.value = !detailOuvert.value
    memoriserDetailOuvert(cleCarte(), detailOuvert.value)
}

// ================================
// COMPUTED
// ================================

/**
 * Abonnement en cours (actif, impayé ou en essai) vu par un administrateur :
 * « Voir les sources » et « Libérer de l'espace » mènent à la liste
 */
const getPeutOuvrirListe = computed(
    () =>
        (props.subscription.status === "active" ||
            props.subscription.status === "past_due" ||
            props.subscription.status === "trialing") &&
        isCompanyAdmin.value
)

/**
 * Récupère le statut de l'abonnement avec son icône et son label
 */
const getStatus = computed(() => {
    const statusLabels: Record<string, string> = {
        active: t("status.active"),
        paused: t("status.paused"),
        canceled: t("status.canceled"),
        past_due: t("status.past_due"),
        trialing: t("status.trialing"),
    }

    // Gravité du BaseTag : en cours = success, en pause = warn, annulé ou
    // impayé = danger
    const statusSeverities: Record<string, string> = {
        active: "success",
        trialing: "success",
        paused: "warn",
        canceled: "danger",
        past_due: "danger",
    }

    const statusIcons: Record<string, string> = {
        active: "pi pi-check",
        paused: "pi pi-pause",
        canceled: "pi pi-ban",
        past_due: "pi pi-exclamation-triangle",
        trialing: "pi pi-clock",
    }

    // Le label du statut sans mention de Stripe (Stripe est une info technique, pas pertinente pour l'utilisateur)
    const label =
        statusLabels[props.subscription.status] || props.subscription.status

    return {
        icon: statusIcons[props.subscription.status] || "pi pi-circle",
        severity: statusSeverities[props.subscription.status] ?? "secondary",
        label,
    }
})

/**
 * Calcule le prix total de l'abonnement (prix de base + prix des modules)
 */
const getPrice = computed(() => {
    const pricingModules =
        props.subscription?.modules?.reduce((acc, module) => {
            acc += module.price
            return acc
        }, 0) || 0

    return formatNumber(props.subscription.price + pricingModules, {
        style: "currency",
        currency: "EUR",
    })
})

/**
 * Récupère le nom formaté de l'abonnement (sans le suffixe après "-")
 */
const getName = computed(() => {
    // Pour les abonnements legacy, on garde le nom complet
    if (props.subscription.legacy) {
        return props.subscription.name
    }

    // Pour les nouveaux abonnements, on retire le texte après "-"
    return (
        props.subscription.name?.split("-")?.[0]?.trim() ??
        props.subscription.name
    )
})

/**
 * Formate l'intervalle de facturation
 */
const getInvoiceInterval = computed(() => {
    const interval = props.subscription.invoice_interval
    const count = props.subscription.invoice_interval_count || 1

    const intervalLabels: Record<string, string> = {
        month: t("facturation.month"),
        year: t("facturation.year"),
        week: t("facturation.week"),
        day: t("facturation.day"),
        quote: t("facturation.quote"),
    }

    if (count > 1) {
        return `${count} ${intervalLabels[interval] || interval}`
    }

    return intervalLabels[interval] || interval
})

/**
 * Formate la date de fin selon si c'est Stripe ou non
 */
const getEndDateLabel = computed(() => {
    return props.subscription.stripe
        ? t("end_date_stripe")
        : t("end_date_no_stripe")
})

/**
 * Forfait affiché : celui du décompte servi, sinon la taille du pack de l'abonnement
 */
const getForfait = computed(
    () => props.plafond?.forfait ?? props.subscription.metadata?.sources ?? 0
)

/**
 * Composition servie et cohérente avec le total servi, sinon `null`
 * (totaux seuls, sans arbre ni phrase de composition)
 */
const getComposition = computed(() => compositionCoherente(props.plafond))

/**
 * Décompte (DEV-621, écran M1, revue UX du 15/10) : forfait, puis une seule
 * phrase de tête (période servie, sources facturées et leur composition,
 * écart au forfait) au-dessus du tableau. Le total affiché est toujours le
 * total servi. Sans nombre servi : forfait et période seuls, et la mention
 * d'indisponibilité ; avant la réponse : aucun nombre.
 */
const getDecompte = computed(() => {
    const p = props.plafond
    const debut = p?.periode_debut ?? null
    const fin = p?.periode_fin ?? null
    const facturees = p?.facturees ?? props.sources
    let factureesTexte: string | null = null
    if (props.decompteLoaded) {
        factureesTexte =
            facturees === null || facturees === undefined
                ? TEXTE_FACTUREES_INDISPONIBLE
                : texteFacturees(facturees, getComposition.value, fin)
    }
    const ecart = p ? texteEcart(p.forfait, p.facturees) : null
    const periode = textePeriode(debut, fin)
    return {
        forfait: texteForfait(getForfait.value),
        periode,
        facturees: factureesTexte,
        ecart,
        // « Période du 30/09 au 30/10. 511 sources facturées : …. 3089 places libres. »
        tete:
            [periode, factureesTexte, factureesTexte ? ecart : null]
                .filter((x): x is string => !!x)
                .join(" ") || null,
        debut,
        fin,
        offre: offreServie(p, authStore.companyStripeSubscriptions),
    }
})
</script>

<template>
    <div
        class="company-subscriptions-list-item flex flex-col gap-4 p-3 border-b border-surface-200 last:border-b-0"
    >
        <!-- ================================ -->
        <!-- TITRE AVEC STATUT -->
        <!-- ================================ -->
        <h3
            class="company-subscriptions-list-item__title flex items-center flex-wrap gap-4 text-lg font-semibold text-primary-800"
        >
            {{ getName }}
            <BaseTag
                class="company-subscriptions-list-item__status"
                :value="getStatus.label"
                :icon="getStatus.icon"
                :severity="getStatus.severity"
            />
        </h3>

        <!-- ================================ -->
        <!-- INFORMATIONS SOURCES ET MODULES -->
        <!-- ================================ -->
        <div
            v-if="!subscription.legacy"
            class="company-subscriptions-list-item__info flex flex-col gap-4"
        >
            <!-- Nombre de sources (réservé aux administrateurs de l'entreprise) -->
            <div
                class="company-subscriptions-list-item__total-source flex items-center flex-wrap gap-3"
            >
                <p
                    v-if="isCompanyAdmin && getForfait > 0"
                    class="company-subscriptions-list-item__forfait text-base font-semibold text-surface-800"
                >
                    {{ getDecompte.forfait }}
                </p>
                <BaseButton
                    v-if="getPeutOuvrirListe"
                    variant="link"
                    size="small"
                    class="w-fit p-0!"
                    :label="t('list_source')"
                    @click="$emit('click:source')"
                />
            </div>

            <!-- Décompte de la période (DEV-621, écran M1) -->
            <div
                v-if="isCompanyAdmin && getForfait > 0"
                class="company-subscriptions-list-item__decompte flex flex-col gap-3 text-sm text-surface-700"
            >
                <!-- Composition servie : une seule phrase de tête -->
                <p
                    v-if="getComposition && getDecompte.tete"
                    class="company-subscriptions-list-item__tete font-medium text-surface-800"
                >
                    {{ getDecompte.tete }}
                </p>
                <!-- Carte de repli sans composition : inchangée -->
                <template v-else>
                    <p v-if="getDecompte.periode">{{ getDecompte.periode }}</p>
                    <p v-if="getDecompte.facturees" class="font-medium">
                        {{ getDecompte.facturees }}
                        <template v-if="getDecompte.ecart">
                            {{ getDecompte.ecart }}
                        </template>
                    </p>
                </template>
                <!-- Barre de synthèse du forfait (demande du 16/10), entre la phrase et le tableau -->
                <CompanySubscriptionDecompteBarre
                    v-if="plafond && getComposition && getDecompte.offre"
                    :composition="getComposition"
                    :facturees="plafond.facturees"
                    :forfait="plafond.forfait"
                    :offre="getDecompte.offre"
                    :fin="getDecompte.fin"
                />
                <!-- Actions de la jauge (DEV-2480) : la liste filtrée sur les sources qui libèrent une place, et le tableau replié -->
                <div
                    v-if="
                        getPeutOuvrirListe ||
                        (plafond && getComposition && getDecompte.offre)
                    "
                    class="company-subscriptions-list-item__actions flex flex-wrap items-center gap-x-4 gap-y-2"
                >
                    <BaseButton
                        v-if="getPeutOuvrirListe"
                        size="small"
                        severity="secondary"
                        outlined
                        class="company-subscriptions-list-item__liberer"
                        :label="t('liberer_espace')"
                        @click="$emit('click:liberer')"
                    />
                    <BaseButton
                        v-if="plafond && getComposition && getDecompte.offre"
                        variant="link"
                        size="small"
                        class="company-subscriptions-list-item__detail w-fit p-0!"
                        :label="
                            detailOuvert
                                ? t('masquer_detail')
                                : t('voir_detail')
                        "
                        :aria-expanded="detailOuvert ? 'true' : 'false'"
                        @click="basculerDetail"
                    />
                </div>
                <!-- Arbre en trois questions (décision du 14/10) : composition servie et cohérente, offre connue -->
                <!-- replié par défaut derrière « Voir le détail » (DEV-2480), jamais supprimé -->
                <!-- de l'air entre les actions et le tableau : 12 px du gap + 16 px = 1,75 rem -->
                <CompanySubscriptionDecompteTree
                    v-if="plafond && getComposition && getDecompte.offre"
                    v-show="detailOuvert"
                    class="company-subscriptions-list-item__arbre mt-4"
                    :composition="getComposition"
                    :facturees="plafond.facturees"
                    :forfait="plafond.forfait"
                    :offre="getDecompte.offre"
                    :debut="getDecompte.debut"
                    :fin="getDecompte.fin"
                    @ouvrir-liste="(f) => $emit('click:filtres', f)"
                />
            </div>

            <!-- Liste des modules -->
            <div
                v-if="subscription.modules?.length"
                class="company-subscriptions-list-item__modules flex flex-col gap-2 border-t border-surface-200 pt-4"
            >
                <div
                    v-for="(subModule, index) in subscription.modules"
                    :key="index"
                    class="company-subscriptions-list-item__modules-item flex items-center flex-wrap gap-3"
                >
                    <p class="text-sm font-medium text-surface-800">
                        {{ subModule.name }}
                    </p>
                    <p v-if="isCompanyAdmin" class="text-sm text-surface-500">
                        {{
                            t("module_price", {
                                value: formatNumber(
                                    subModule.price /
                                        (subModule.invoice_interval_count ?? 1),
                                    {
                                        style: "currency",
                                        currency: "EUR",
                                    }
                                ),
                                interval:
                                    subModule.invoice_interval === "month"
                                        ? "mois"
                                        : subModule.invoice_interval === "year"
                                          ? "an"
                                          : subModule.invoice_interval,
                            })
                        }}
                    </p>
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- CHAMPS D'INFORMATION -->
        <!-- ================================ -->
        <div
            class="company-subscriptions-list-item__fields grid gap-3 py-2"
            :class="{
                'grid-cols-1': true,
                'md:grid-cols-2': true,
                'lg:grid-cols-4': true,
            }"
        >
            <div class="flex flex-col gap-1.5">
                <p class="text-xs text-surface-500">
                    {{ t("fields.price") }}
                </p>
                <p class="text-sm text-surface-800">
                    <span v-if="isCompanyAdmin">{{ getPrice }} HT</span>
                    <span v-else>-</span>
                </p>
            </div>

            <div class="flex flex-col gap-1.5">
                <p class="text-xs text-surface-500">
                    {{ t("fields.facturation") }}
                </p>
                <p class="text-sm text-surface-800">{{ getInvoiceInterval }}</p>
            </div>

            <div class="flex flex-col gap-1.5">
                <p class="text-xs text-surface-500">
                    {{ t("fields.start_date") }}
                </p>
                <p class="text-sm text-surface-800">
                    {{ formatDate(subscription.date_start, "DD/MM/YYYY", "-") }}
                </p>
            </div>

            <div class="flex flex-col gap-1.5">
                <p class="text-xs text-surface-500">
                    {{ getEndDateLabel }}
                </p>
                <p class="text-sm text-surface-800">
                    {{ formatDate(subscription.date_end, "DD/MM/YYYY", "-") }}
                </p>
            </div>
        </div>
    </div>
</template>
